import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaList, FaMapMarkedAlt } from "react-icons/fa";
import SEO from "../components/SEO";
import TutorCenterList from "../components/tutorCenters/TutorCenterList";
import TutorCenterMap from "../components/tutorCenters/TutorCenterMapPanel";
import TutorCenterSearch from "../components/tutorCenters/TutorCenterSearch";
import hkLocations from "../i18n/hong_kong_locations.json";
import mainData from "../jsonDB/main.json";
import mediaData from "../jsonDB/media_updated.json";

function TutorCenters() {
  const [tutorCenters, setTutorCenters] = useState([]);
  const [filteredCenters, setFilteredCenters] = useState([]);
  const [districtsList, setDistrictsList] = useState([]);
  const [subjectsList, setSubjectsList] = useState([]);
  // selectedCenter is now handled on a separate detail page; navigation handled by list
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list"); // "list" or "map"

  const { t } = useTranslation();

  useEffect(() => {
    // Merge local JSON modules (main + media) by `id`, and fetch address mapping from public
    const loadData = async () => {
      // build media map by id for quick lookup
      const mediaMap = (mediaData || []).reduce((acc, m) => {
        if (m && m.id) acc[m.id] = m;
        return acc;
      }, {});

      // Merge mainData with media entries (media may contain images/tags)
      // Accept both shapes: an array (legacy) or an object { markers: [...] } (newer)
      const rawMain = Array.isArray(mainData)
        ? mainData
        : (mainData && mainData.markers) || [];
      const merged = (rawMain || []).map((item) => {
        const media = item?.id ? mediaMap[item.id] : null;
        return {
          // keep main as source of truth, but attach images/product_tags/url if missing
          ...item,
          images:
            item.images && item.images.length > 0
              ? item.images
              : (media && media.images) || [],
          product_tags:
            item.product_tags && item.product_tags.length > 0
              ? item.product_tags
              : (media && media.product_tags) || [],
          url: item.url || (media && media.url) || "",
        };
      });

      // Set centers first so UI shows even if address fetch fails
      try {
        setTutorCenters(merged);
        setFilteredCenters(merged);
        // build regions and subjects lists
        // compute frequency maps (use district as requested)
        const districtCount = {};
        const subjectCount = {};
        merged.forEach((c) => {
          if (c.district)
            districtCount[c.district] = (districtCount[c.district] || 0) + 1;
          if (c.product_tags && c.product_tags.length > 0) {
            c.product_tags.forEach(
              (tag) => (subjectCount[tag] = (subjectCount[tag] || 0) + 1)
            );
          }
        });
        const districtsArr = Object.keys(districtCount).sort((a, b) => {
          const diff = districtCount[b] - districtCount[a];
          if (diff !== 0) return diff;
          return a.localeCompare(b);
        });

        const subjectsArr = Object.keys(subjectCount).sort((a, b) => {
          const diff = subjectCount[b] - subjectCount[a];
          if (diff !== 0) return diff;
          return a.localeCompare(b);
        });

        setDistrictsList(districtsArr);
        setSubjectsList(subjectsArr);
        console.log(`TutorCenters: loaded ${merged.length} items`);
        // Build area->region mapping from hkLocations for region-level filtering
        try {
          const areaToRegion = {};
          (hkLocations.regions || []).forEach((r) => {
            (r.areas || []).forEach((a) => {
              areaToRegion[a.value] = r.value;
            });
          });
          // store mapping in state so handleSearch can use it
          // but it's fine to attach to window for simplicity here
          window.__hk_area_to_region = areaToRegion;
        } catch (e) {
          console.warn("Failed to build area->region mapping", e);
        }
      } catch (e) {
        console.error("Error setting merged centers:", e);
      }

      // address mapping removed; using center.address (from main.json) directly

      setLoading(false);
    };

    loadData();
    // restore scroll position if present (returning from detail)
    try {
      const v = sessionStorage.getItem("tutorCenters_scroll");
      if (v) {
        const num = Number(v) || 0;
        setTimeout(() => {
          try {
            window.scrollTo(0, num);
          } catch (e) {}
        }, 40);
        sessionStorage.removeItem("tutorCenters_scroll");
      }
    } catch (e) {}
  }, []);

  // searchObj: { searchTerm, district, districts, subject }
  const handleSearch = useCallback(
    (searchObj) => {
      const {
        searchTerm = "",
        district = "",
        districts = [],
        subject = "",
      } = typeof searchObj === "string"
        ? { searchTerm: searchObj }
        : searchObj || {};

      // console.log("TutorCenters: handleSearch", { searchTerm, district, districts, subject });

      const term = (searchTerm || "").toLowerCase().trim();

      let filtered = tutorCenters.filter((center) => {
        // districts filter (multi-select support)
        if (districts && districts.length > 0) {
          // Check if center's districtValue matches any selected district
          const centerDistrictValue =
            center.districtValue || center.district || "";
          if (!districts.includes(centerDistrictValue)) {
            return false;
          }
        } else if (district) {
          // fallback to single district filter
          const centerDistrictValue =
            center.districtValue || center.district || "";
          if (centerDistrictValue !== district) return false;
        }
        // subject/tag filter
        if (
          subject &&
          !(
            center.product_tags &&
            center.product_tags.some((tag) => tag === subject)
          )
        )
          return false;

        if (!term) return true; // no text filter, passes

        // Search in title
        if (center.title && center.title.toLowerCase().includes(term)) {
          return true;
        }
        // Search in content_text
        if (
          center.content_text &&
          center.content_text.toLowerCase().includes(term)
        ) {
          return true;
        }
        // Search in product_tags (subject)
        if (
          center.product_tags &&
          center.product_tags.some((tag) => tag.toLowerCase().includes(term))
        ) {
          return true;
        }
        // Search in region/district/address
        if (center.district && center.district.toLowerCase().includes(term))
          return true;
        if (center.region && center.region.toLowerCase().includes(term))
          return true;
        if (center.address && center.address.toLowerCase().includes(term))
          return true;
        // Search in other contact fields (phone/contact)
        if (center.phone && center.phone.toLowerCase().includes(term))
          return true;
        if (center.contact && center.contact.toLowerCase().includes(term))
          return true;
        // Search in WhatsApp number (digits-insensitive) - guard empty digit search
        if (center.whatsapp_number) {
          const cleaned = String(center.whatsapp_number).replace(/\D/g, "");
          const termDigits = (term || "").replace(/\D/g, "");
          if (termDigits && cleaned.includes(termDigits)) return true;
        }

        return false;
      });

      setFilteredCenters(filtered);
    },
    [tutorCenters]
  );

  if (loading) {
    return (
      <div className="page p-4 flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t("tutorCenters.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page p-4">
      <SEO
        title="Tutor Centers | 補習中心"
        description="Search and find tutor centers in Hong Kong. Browse through various tutoring centers and their services."
      />

      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {t("tutorCenters.title")}
          </h1>
          <p className="text-gray-600">{t("tutorCenters.description")}</p>
        </div>

        <>
          <TutorCenterSearch
            onSearch={handleSearch}
            districts={districtsList}
            subjects={subjectsList}
          />

          {/* View Mode Toggle */}
          <div className="flex justify-end mb-4 text-sm gap-2">
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === "list"
                  ? "bg-orange-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <FaList />
              {t("tutorCenters.listView") || "列表模式"}
            </button>
            <button
              onClick={() => setViewMode("map")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === "map"
                  ? "bg-orange-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <FaMapMarkedAlt />
              {t("tutorCenters.mapView") || "地圖模式"}
            </button>
          </div>

          {viewMode === "list" ? (
            <TutorCenterList centers={filteredCenters} />
          ) : (
            <TutorCenterMap
              hkLocations={hkLocations}
              centers={filteredCenters}
            />
          )}
        </>
      </div>
    </div>
  );
}

export default TutorCenters;
